/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Entity Labels Hook
 *
 * Provides a reusable hook for deriving singular/plural and lowercase variations
 * of entity labels (e.g., "service", "document", "client") using industry
 * terminology overrides and pluralization.
 *
 * This hook eliminates the need for repeated label derivation logic across pages.
 * It automatically:
 * - Checks for custom terminology overrides from `useIndustry().terminology.customTerms`
 * - Falls back to the provided `defaultLabel` or capitalizes the key
 * - Generates all case variations using the existing `pluralize` utility
 *
 * @example
 * ```tsx
 * // Basic usage with default
 * const labels = useEntityLabels("service", "Service");
 * // Returns:
 * // {
 * //   singular: "Service" (or custom override from terminology),
 * //   plural: "Services",
 * //   singularLower: "service",
 * //   pluralLower: "services"
 * // }
 *
 * // Usage in UI:
 * <Button>Add {labels.singular}</Button>
 * <p>Loading {labels.pluralLower}...</p>
 * <h1>{labels.plural}</h1>
 * ```
 *
 * @example
 * ```tsx
 * // Without default (uses capitalized key)
 * const labels = useEntityLabels("document");
 * // If no customTerm, returns: { singular: "Document", plural: "Documents", ... }
 * ```
 *
 * @param key - The terminology key (e.g., "service", "document", "client")
 *               This key is checked in `terminology.customTerms[key]` for overrides
 * @param defaultLabel - Optional default label if key is not found in customTerms.
 *                       If omitted, the key will be capitalized (e.g., "service" → "Service")
 * @returns Object with singular, plural, singularLower, and pluralLower variations
 *
 * @see {@link useIndustry} - Provides terminology context
 * @see {@link getLabelVariations} - Underlying pluralization utility
 */

import { useIndustry } from "@/contexts/industry-context";
import {
  getLabelVariations,
  type LabelVariations,
} from "@/lib/utils/pluralize";
import { useMemo } from "react";

/**
 * Type alias for entity label variations returned by useEntityLabels
 * Re-exports LabelVariations for convenience
 */
export type EntityLabels = LabelVariations;

export function useEntityLabels(
  key: string,
  defaultLabel?: string,
): EntityLabels {
  const { terminology } = useIndustry();

  return useMemo(() => {
    // Get the base label from customTerms or use provided default or key capitalized
    const baseLabel =
      terminology.customTerms?.[key] ??
      defaultLabel ??
      key.charAt(0).toUpperCase() + key.slice(1);

    // Use existing pluralize utility to get all variations
    return getLabelVariations(baseLabel);
  }, [terminology.customTerms, key, defaultLabel]);
}
